// Lib
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Components
import ContextMenuItem from '../ContextMenuItem';

// Utils
import { getImageSource } from '../../../element/image/imageHelper';
import { getElementId, getImageProp, getFileUrl } from '../../../../common/elements/utils/elementPropertyUtils';
import { getIsFeatureEnabledForCurrentUser } from '../../../element/feature/elementFeatureSelector';
import { isFile, isImage } from '../../../../common/elements/utils/elementTypeUtils';
import { triggerDownload } from '../../../element/attachments/attachmentDownloadActions';
import { useTranslator } from '../../../components/i18n/useTranslator';

// Constants
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';

const mapStateToProps = createStructuredSelector({
    renderSecureImagesThroughProxy: getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughProxy),
    // Default to enabled for guest users
    renderSecureImagesThroughCF: getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF, true),
});

const mapDispatchToProps = (dispatch) => ({
    dispatchTriggerDownload: (params) => dispatch(triggerDownload(params)),
});

const DownloadOriginalContextMenuItem = ({
    contextMenuHandlers,
    allImages,
    singleImage,
    selectedElements,
    renderSecureImagesThroughProxy,
    renderSecureImagesThroughCF,
    dispatchTriggerDownload,
}) => {
    const translate = useTranslator('canvas', 'contextMenuItems.element');

    const download = useCallback(
        () =>
            selectedElements.forEach((el) => {
                let url;

                if (isFile(el)) {
                    url = getFileUrl(el);
                } else {
                    url = getImageSource({
                        imageSize: 'original',
                        imageDetails: getImageProp(el),
                        elementId: getElementId(el),
                        useSecureMediaUrl: true,
                        renderSecureImagesThroughProxy,
                        renderSecureImagesThroughCF,
                        optimizeForWebRender: false,
                    });
                }

                if (url) dispatchTriggerDownload({ url, id: getElementId(el) });
            }),
        [selectedElements],
    );

    const showDownloadOption = selectedElements.every((el) => isFile(el) || isImage(el));

    // Determine the title based on whether it's a single image, all images, or files
    let title;
    if (singleImage) {
        title = translate('downloadOriginalImage');
    } else if (allImages) {
        title = translate('downloadOriginalImages');
    } else {
        title = translate('download');
    }

    return (
        <ContextMenuItem
            {...contextMenuHandlers}
            title={title}
            id="downloadOriginal"
            show={showDownloadOption}
            onClick={download}
        />
    );
};

DownloadOriginalContextMenuItem.propTypes = {
    contextMenuHandlers: PropTypes.object,
    allImages: PropTypes.bool,
    selectedElements: PropTypes.object,
    singleImage: PropTypes.bool,
    renderSecureImagesThroughProxy: PropTypes.bool,
    renderSecureImagesThroughCF: PropTypes.bool,
    dispatchTriggerDownload: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(DownloadOriginalContextMenuItem);
